(() => {
  const config = window.SnappyTheme || {};
  const routes = config.routes || {};
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]);
  const money = (cents = 0) => {
    const amount = (Number(cents) / 100).toFixed(2);
    const format = String(config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    return format
      .replace(/{{\s*amount_no_decimals\s*}}/g, Math.round(Number(cents) / 100).toString())
      .replace(/{{\s*amount\s*}}/g, amount)
      .replace(/{{\s*amount_with_comma_separator\s*}}/g, amount.replace('.', ','));
  };

  class SnappyStore {
    constructor() {
      this.cartDrawer = qs('[data-cart-drawer]');
      this.cartBackdrop = qs('[data-cart-backdrop]');
      this.bindNavigation();
      this.bindCart();
      this.bindGames();
      this.bindDemos();
      this.bindProducts();
      this.bindReveals();
    }

    bindNavigation(scope = document) {
      const button = qs('[data-mobile-menu-button]', scope);
      const menu = qs('[data-mobile-menu]', scope);
      if (!button || !menu) return;
      if (button.dataset.snappyBound) return;
      button.dataset.snappyBound = 'true';
      const close = () => {
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('menu-open');
      };
      button.addEventListener('click', () => {
        const opening = menu.hidden;
        menu.hidden = !opening;
        button.setAttribute('aria-expanded', String(opening));
        document.body.classList.toggle('menu-open', opening);
      });
      qsa('a', menu).forEach((link) => link.addEventListener('click', close));
      window.addEventListener('resize', () => { if (window.innerWidth > 900) close(); });
    }

    bindCart() {
      document.addEventListener('click', async (event) => {
        if (event.target.closest('[data-cart-open]')) {
          event.preventDefault();
          await this.openCart();
        }
        if (event.target.closest('[data-cart-close]') || event.target.matches('[data-cart-backdrop]')) this.closeCart();
        const change = event.target.closest('[data-cart-change]');
        if (change) {
          change.disabled = true;
          await this.changeCart(Number(change.dataset.cartChange), Number(change.dataset.quantity));
        }
      });
      document.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeCart(); });
    }

    async cartRequest(endpoint, options) {
      const response = await fetch(endpoint, options);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Shopify could not update the bag.');
      }
      return response.json();
    }

    async getCart() {
      return this.cartRequest(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    }

    async openCart() {
      if (!this.cartDrawer || !this.cartBackdrop) return;
      this.cartDrawer.classList.add('open');
      this.cartBackdrop.hidden = false;
      requestAnimationFrame(() => this.cartBackdrop.classList.add('open'));
      this.cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-open');
      try { this.renderCart(await this.getCart()); } catch (error) { this.renderCartError(error.message); }
      qs('[data-cart-close]', this.cartDrawer)?.focus();
    }

    closeCart() {
      if (!this.cartDrawer || !this.cartBackdrop) return;
      this.cartDrawer.classList.remove('open');
      this.cartBackdrop.classList.remove('open');
      this.cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-open');
      window.setTimeout(() => { this.cartBackdrop.hidden = true; }, 250);
    }

    async changeCart(line, quantity) {
      try {
        const cart = await this.cartRequest(`${routes.cartChange || '/cart/change'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity }),
        });
        this.renderCart(cart);
      } catch (error) { this.renderCartError(error.message); }
    }

    updateCartCount(count) {
      qsa('[data-cart-count]').forEach((element) => { element.textContent = count; });
      const opener = qs('[data-cart-open]');
      if (opener) opener.setAttribute('aria-label', `Open shopping bag with ${count} items`);
    }

    renderCartError(message) {
      const content = qs('[data-cart-drawer-content]');
      if (content) content.innerHTML = `<div class="empty-cart"><span>!</span><h3>Bag update paused.</h3><p>${escapeHTML(message)}</p></div>`;
    }

    renderCart(cart) {
      this.updateCartCount(cart.item_count || 0);
      const content = qs('[data-cart-drawer-content]');
      if (!content) return;
      if (!cart.item_count) {
        content.innerHTML = `<div class="empty-cart"><span>〰</span><h3>No snacks in here yet.</h3><p>Choose a hungry hero to start your game.</p><a class="primary-button" href="${escapeHTML(routes.root || '/')}#shop">Choose Your Snake</a></div>`;
        return;
      }
      const items = cart.items.map((item, index) => {
        const image = item.featured_image?.url || item.image;
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${escapeHTML(item.variant_title)}</p>` : '';
        return `<div class="cart-item">${image ? `<img src="${escapeHTML(image)}&width=220" width="100" height="100" alt="${escapeHTML(item.product_title)}">` : ''}<div><h3>${escapeHTML(item.product_title)}</h3>${variant}<strong class="cart-line-price">${money(item.final_line_price)}</strong><div class="cart-qty"><button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Increase quantity">+</button></div></div><button class="remove" type="button" data-cart-change="${index + 1}" data-quantity="0">Remove</button></div>`;
      }).join('');
      content.innerHTML = `${items}<div class="cart-confirm"><div class="cart-subtotal"><span>Subtotal</span><strong data-cart-total>${money(cart.total_price)}</strong></div><p>Shipping, taxes, and discounts are calculated at checkout.</p><a class="primary-button" href="${escapeHTML(routes.cart || '/cart')}">View Cart</a><a class="buy-now cart-checkout" href="/checkout">Secure Checkout</a></div>`;
    }

    bindGames(scope = document) {
      qsa('[data-game-frame]', scope).forEach((frame) => {
        if (frame.dataset.snappyBound) return;
        frame.dataset.snappyBound = 'true';
        qs('[data-game-enter]', frame)?.addEventListener('click', () => {
          frame.classList.add('is-playing');
          document.body.classList.add('game-playing');
        });
        qs('[data-game-exit]', frame)?.addEventListener('click', () => {
          frame.classList.remove('is-playing');
          document.body.classList.remove('game-playing');
          frame.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }

    bindDemos(scope = document) {
      qsa('[data-demo-toggle]', scope).forEach((button) => {
        if (button.dataset.snappyBound) return;
        button.dataset.snappyBound = 'true';
        button.addEventListener('click', () => {
        const card = button.closest('article');
        const active = card.classList.toggle('is-previewing');
        button.textContent = active ? 'Ⅱ' : '▶';
        button.setAttribute('aria-pressed', String(active));
        const caption = qs('[data-demo-caption]', card);
        if (caption) caption.textContent = active ? 'Staged brand demonstration' : 'Tap play to preview';
        });
      });
    }

    bindProducts(scope = document) {
      qsa('[data-product-section]', scope).forEach((section) => {
        if (section.dataset.snappyBound) return;
        section.dataset.snappyBound = 'true';
        this.setupProduct(section);
      });
    }

    setupProduct(section) {
      const json = qs('[data-product-json]', section);
      if (!json) return;
      let variants = [];
      try { variants = JSON.parse(json.textContent); } catch (_) { return; }
      const variantMap = new Map(variants.map((variant) => [String(variant.id), variant]));
      let bundleSize = 1;
      const rows = qs('[data-bundle-colors]', section);
      const form = qs('.snappy-product-form', section);
      const status = qs('[data-product-status]', section);
      const firstTemplate = qs('[data-set-index="0"]', section);
      const selectedButtons = () => qsa('[data-set-index]', section).map((row) => qs('[data-variant-id].selected', row)).filter(Boolean);
      const total = () => selectedButtons().reduce((sum, button) => sum + Number(variantMap.get(button.dataset.variantId)?.price || 0), 0);
      const discountedTotal = () => Math.round(total() * (bundleSize === 2 ? 0.9 : bundleSize === 3 ? 0.85 : 1));
      const update = () => {
        const chosen = selectedButtons();
        const first = variantMap.get(chosen[0]?.dataset.variantId);
        if (!first) return;
        const available = chosen.length === bundleSize && chosen.every((button) => variantMap.get(button.dataset.variantId)?.available);
        const price = discountedTotal();
        qs('[data-variant-input]', section).value = first.id;
        qsa('[data-button-price], [data-buy-price], [data-sticky-price]', section).forEach((node) => { node.textContent = money(price); });
        const label = qs('[data-selected-color]', section);
        if (label) label.textContent = first.option1 || first.title;
        const stickySummary = qs('[data-sticky-summary]', section);
        if (stickySummary) stickySummary.textContent = `${bundleSize} ${bundleSize === 1 ? 'set' : 'sets'} · ${chosen.map((button) => button.dataset.variantTitle).join(' + ')}`;
        const image = qs('[data-product-media] img', section);
        if (image && chosen[0].dataset.variantImage) image.src = chosen[0].dataset.variantImage;
        qsa('[data-add-bundle], [data-buy-now], [data-sticky-add]', section).forEach((button) => { button.disabled = !available; });
        if (status) status.textContent = available ? '' : 'One or more selected colors are sold out.';
      };
      const setBundle = (size) => {
        bundleSize = size;
        qsa('[data-bundle]', section).forEach((button) => {
          const selected = Number(button.dataset.bundle) === size;
          button.classList.toggle('selected', selected);
          button.setAttribute('aria-pressed', String(selected));
        });
        if (rows && firstTemplate) {
          while (rows.children.length > 1) rows.lastElementChild.remove();
          for (let index = 1; index < size; index += 1) {
            const clone = firstTemplate.cloneNode(true);
            clone.dataset.setIndex = String(index);
            qs('span', clone).textContent = `${section.dataset.setLabel || 'Set'} ${index + 1}`;
            rows.appendChild(clone);
          }
        }
        const legend = qs('[data-color-legend]', section);
        if (legend) legend.textContent = size === 1
          ? (section.dataset.colorSingle || 'Choose your color')
          : (section.dataset.colorMultiple || 'Choose colors for {count} sets').replace('{count}', size);
        update();
      };
      qsa('[data-bundle]', section).forEach((button) => button.addEventListener('click', () => setBundle(Number(button.dataset.bundle))));
      rows?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-variant-id]');
        if (!button) return;
        const row = button.closest('[data-set-index]');
        qsa('[data-variant-id]', row).forEach((choice) => {
          const selected = choice === button;
          choice.classList.toggle('selected', selected);
          choice.setAttribute('aria-pressed', String(selected));
        });
        update();
      });
      const items = () => {
        const grouped = new Map();
        selectedButtons().forEach((button) => grouped.set(button.dataset.variantId, (grouped.get(button.dataset.variantId) || 0) + 1));
        return [...grouped].map(([id, quantity]) => ({ id: Number(id), quantity, properties: { _Bundle: `${bundleSize} ${bundleSize === 1 ? 'set' : 'sets'}` } }));
      };
      const add = async (checkout = false) => {
        const lineItems = items();
        if (!lineItems.length) return;
        qsa('[data-add-bundle], [data-buy-now], [data-sticky-add]', section).forEach((button) => { button.disabled = true; });
        if (status) status.textContent = 'Adding your hungry hero…';
        try {
          await this.cartRequest(`${routes.cartAdd || '/cart/add'}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: lineItems }),
          });
          if (status) status.textContent = 'Added to your Chomp Bag!';
          if (checkout) window.location.assign('/checkout');
          else await this.openCart();
        } catch (error) { if (status) status.textContent = error.message; }
        finally { update(); }
      };
      form?.addEventListener('submit', (event) => { event.preventDefault(); add(false); });
      qs('[data-buy-now]', section)?.addEventListener('click', () => add(true));
      qs('[data-sticky-add]', section)?.addEventListener('click', () => add(false));
      qs('[data-sticky-close]', section)?.addEventListener('click', () => { qs('[data-sticky-cart]', section).hidden = true; });
      const sticky = qs('[data-sticky-cart]', section);
      if (sticky && form && 'IntersectionObserver' in window) {
        let dismissed = false;
        qs('[data-sticky-close]', sticky)?.addEventListener('click', () => { dismissed = true; });
        new IntersectionObserver(([entry]) => { if (!dismissed) sticky.hidden = entry.isIntersecting; }, { threshold: 0.2 }).observe(form);
      }
      update();
    }

    bindReveals(scope = document) {
      const elements = qsa('[data-reveal]', scope).filter((element) => !element.dataset.snappyRevealBound);
      elements.forEach((element) => { element.dataset.snappyRevealBound = 'true'; });
      if (!('IntersectionObserver' in window)) { elements.forEach((element) => element.classList.add('is-revealed')); return; }
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); }
      }), { rootMargin: '0px 0px -8% 0px' });
      elements.forEach((element) => observer.observe(element));
    }
  }

  let app;
  const boot = () => { app = new SnappyStore(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => {
    if (!app) return;
    app.cartDrawer = qs('[data-cart-drawer]');
    app.cartBackdrop = qs('[data-cart-backdrop]');
    app.bindNavigation(event.target);
    app.bindGames(event.target);
    app.bindDemos(event.target);
    app.bindProducts(event.target);
    app.bindReveals(event.target);
  });
})();
